import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useStudentNotesListQuery,
  useGroupNotesListQuery,
  useAddNoteMutation,
  useEditNoteMutation,
  useRemoveNoteMutation,
  StudentNotesListDocument,
  GroupNotesListDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

export interface INote {
  id: string;
  body: string;
  sentiment: string;
  pinned: boolean;
  createdAt: string;
  authorName: string | null;
}

interface IUseNotesArgs {
  studentId?: string;
  groupId?: string;
}

interface IUseNotes {
  notes: INote[];
  loading: boolean;
  saving: boolean;
  add: (args: {
    body: string;
    sentiment?: string;
    pinned?: boolean;
  }) => Promise<boolean>;
  edit: (args: {
    id: string;
    body?: string;
    sentiment?: string;
    pinned?: boolean;
  }) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
}

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * Notes for a student OR a group (spec 0003 Phase 2). One of `studentId` /
 * `groupId` selects which query runs; mutations refetch that list so the panel
 * repaints. Errors surface as toasts.
 */
export const useNotes = ({ studentId, groupId }: IUseNotesArgs): IUseNotes => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const studentQuery = useStudentNotesListQuery({
    variables: { studentId: studentId || '' },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
  });
  const groupQuery = useGroupNotesListQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });

  const refetchQueries = useMemo(() => {
    if (studentId) {
      return [{ query: StudentNotesListDocument, variables: { studentId } }];
    }
    if (groupId) {
      return [{ query: GroupNotesListDocument, variables: { groupId } }];
    }
    return [];
  }, [studentId, groupId]);
  const opts = { refetchQueries, awaitRefetchQueries: true };

  const [addMut, addState] = useAddNoteMutation(opts);
  const [editMut, editState] = useEditNoteMutation(opts);
  const [removeMut, removeState] = useRemoveNoteMutation(opts);

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.notes.failed'),
          text,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast, t],
  );

  const add = useCallback(
    async (args: { body: string; sentiment?: string; pinned?: boolean }) => {
      try {
        const res = await addMut({
          variables: {
            input: {
              studentId: studentId || undefined,
              groupId: groupId || undefined,
              body: args.body,
              sentiment: args.sentiment,
              pinned: args.pinned,
            },
          },
        });
        const p = res.data?.createStudentNote;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [addMut, studentId, groupId, notifyError],
  );

  const edit = useCallback(
    async (args: {
      id: string;
      body?: string;
      sentiment?: string;
      pinned?: boolean;
    }) => {
      try {
        const res = await editMut({ variables: { input: args } });
        const p = res.data?.updateStudentNote;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [editMut, notifyError],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        const res = await removeMut({ variables: { id } });
        const p = res.data?.deleteStudentNote;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [removeMut, notifyError],
  );

  const notes = (studentId
    ? studentQuery.data?.studentNotes
    : groupQuery.data?.groupNotes) as INote[] | undefined;

  return {
    notes: notes || [],
    loading: studentId ? studentQuery.loading : groupQuery.loading,
    saving: addState.loading || editState.loading || removeState.loading,
    add,
    edit,
    remove,
  };
};
